<template>
  <Dialog v-model:open="open">
    <DialogContent
      @escape-key-down="preventRequiredDismiss"
      @interact-outside="preventRequiredDismiss"
      :show-close-button="false"
      v-bind="description ? {} : { 'aria-describedby': undefined }"
      @open-auto-focus="!autoFocus && $event.preventDefault()"
      class="tw:w-[calc(100%-var(--dialog-inline-gap))] tw:max-w-96 tw:sm:max-w-96 tw:max-h-[min(88dvh,calc(100dvh-2*var(--app-safe-top)))] tw:overflow-y-auto tw:scroll-py-5 tw:rounded-(--radius-dialog) tw:border-0 tw:p-5 tw:bg-card tw:shadow-(--shadow-dialog)"
      :class="{
        'tw:flex tw:flex-col tw:overflow-hidden': scrollBody && !fullScreen,
        'tw:top-[var(--dialog-viewport-top)] tw:translate-y-0 tw:flex tw:flex-col tw:w-full tw:max-w-[var(--app-frame-width)] tw:sm:max-w-[var(--app-frame-width)] tw:h-[var(--dialog-viewport-height)] tw:max-h-dvh tw:gap-0 tw:border-0 tw:p-0 tw:rounded-none tw:bg-[var(--surface-dialog)]':
          fullScreen,
      }"
      :style="{
        '--dialog-viewport-height': viewportHeight + 'px',
        '--dialog-viewport-top': viewportTop + 'px',
      }"
    >
      <slot name="illustration" />
      <DialogHeader
        class="dialog__header tw:text-center tw:sm:text-center"
        :class="{
          'tw:sr-only': titleHidden,
          'tw:pt-6': !$slots.illustration && !fullScreen,
          'dialog__header--full-screen': fullScreen,
          'tw:shrink-0': scrollBody,
        }"
        ><div v-if="$slots.leading" class="dialog__leading"><slot name="leading" /></div>
        <div
          class="dialog__title-row tw:min-w-0 tw:flex tw:items-center tw:justify-center tw:gap-2"
        >
          <DialogTitle
            class="tw:min-w-0 tw:type-dialog"
            :class="{ 'tw:type-dialog-full': fullScreen }"
            ><slot name="title">{{ title }}</slot></DialogTitle
          ><slot name="title-actions" />
        </div>
        <DialogDescription
          v-if="description"
          class="tw:type-dialog-body tw:whitespace-pre-line"
          :class="[descriptionClass, { 'dialog__description--full-screen': fullScreen }]"
          ><slot name="description">{{ description }}</slot></DialogDescription
        ></DialogHeader
      >
      <DialogClose v-if="dismissible" as-child
        ><Button
          variant="close"
          size="icon-close"
          class="tw:absolute tw:top-3.5 tw:right-3.5"
          :class="{
            'dialog__close--full-screen tw:border-[1.5px] tw:border-border tw:bg-card tw:text-foreground tw:shadow-[var(--shadow-soft)]':
              fullScreen,
          }"
          :aria-label="t('nkf.close')"
          ><AppIcon size="action" v-if="fullScreen" name="x" /><AppIcon
            size="action"
            v-else
            name="x" /></Button
      ></DialogClose>
      <div
        ref="body"
        class="dialog__body tw:min-w-0 tw:grid tw:gap-4 tw:type-content"
        :class="{
          'dialog__body--full-screen': fullScreen,
          'tw:min-h-0 tw:overflow-y-auto': scrollBody && !fullScreen,
        }"
      >
        <slot />
      </div>
      <AppDialogActions
        v-if="$slots.footer || $slots['secondary-actions']"
        class="dialog__footer"
        :class="{ 'dialog__footer--full-screen': fullScreen, 'tw:shrink-0': scrollBody }"
      >
        <template #secondary><slot name="secondary-actions" /></template>
        <slot name="footer" />
      </AppDialogActions>
    </DialogContent>
  </Dialog>
</template>
<script setup>
// 1. Import necessary components
import { computed, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';
import AppDialogActions from './AppDialogActions.vue';
import { useEventListener, useWindowSize } from '@vueuse/core';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { useFocusVisibility } from '@/shared/composables/useFocusVisibility';
import AppIcon from './AppIcon.vue';
// 2. Use
const open = defineModel('open', { type: Boolean, default: false });
const props = defineProps({
  title: { type: String, required: true },
  titleHidden: { type: Boolean, default: false },
  description: { type: String, default: '' },
  descriptionClass: { type: [String, Object, Array], default: '' },
  fullScreen: { type: Boolean, default: false },
  // Long choice lists keep the heading, Close and action visible while the body scrolls.
  scrollBody: { type: Boolean, default: false },
  // A required dialog has no close button and ignores Escape and outside taps.
  dismissible: { type: Boolean, default: true },
  // When off, opening the dialog does not move focus to its first control.
  autoFocus: { type: Boolean, default: true },
});
const { t } = useI18n();
const { height: viewportHeight } = useWindowSize({ type: 'visual' });
// 3. Define a reactive variable
const viewportTop = shallowRef(0);
const body = shallowRef(null);
// 4. Define methods or computed properties if needed
const scrollContainer = computed(() => {
  if (props.fullScreen || props.scrollBody) {
    return body.value;
  }

  // Centered dialogs scroll the frame; full-screen dialogs scroll only the body.
  return body.value?.parentElement;
});

function updateViewportTop() {
  viewportTop.value = window.visualViewport.offsetTop;
}

function preventRequiredDismiss(event) {
  if (!props.dismissible) {
    event.preventDefault();
  }
}
// 5. VueJS Hooks
useFocusVisibility(scrollContainer);
// iOS pans the visual viewport when focusing an input near the bottom.
useEventListener(window.visualViewport, ['resize', 'scroll'], updateViewportTop);
</script>
<style scoped>
.dialog__header--full-screen {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  align-content: center;
  column-gap: 0.75rem;
  row-gap: 0;
  flex: none;
  padding: calc(var(--app-safe-top) + 1rem) 3.75rem 0.75rem var(--page-gutter);
  background: linear-gradient(180deg, var(--card), var(--surface-dialog));
  border-bottom: 1px solid var(--secondary);
  text-align: start;
}
.dialog__header--full-screen .dialog__title-row {
  grid-column: 2;
  grid-row: 1;
  justify-content: flex-start;
}
.dialog__leading {
  grid-row: 1 / span 2;
  grid-column: 1;
}
.dialog__description--full-screen {
  font: var(--type-caption);
  grid-column: 2;
  grid-row: 2;
}
.dialog__close--full-screen {
  top: calc(var(--app-safe-top) + 20px);
  right: var(--page-gutter);
}
.dialog__body:empty {
  display: none;
}
.dialog__body--full-screen {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px var(--page-gutter) 8px;
}
.dialog__footer--full-screen {
  flex: none;
  gap: 0;
  padding: 0 0 max(1.625rem, var(--app-safe-bottom));
}
</style>
